import type { Editor } from '@tiptap/react';
import { getMarkRange } from '@tiptap/react';

/**
 * Returns the bounding client rect of the link at the current selection,
 * or null if the selection is not inside a link.
 */
export function getLinkBoundingRect(editor: Editor | null): DOMRect | null {
    if (!editor || editor.isDestroyed) {
        return null;
    }

    const { schema, doc, selection } = editor.state;
    const linkMark = schema.marks.link;
    if (!linkMark) {
        return null;
    }

    const range = getMarkRange(doc.resolve(selection.from), linkMark);
    if (!range) {
        return null;
    }

    return getLinkBoundingRectFromRange(editor, range);
}

/**
 * Returns the document range (from, to) of the link at the current selection,
 * or null if the selection is not inside a link.
 */
export function getLinkRange(editor: Editor | null): { from: number; to: number } | null {
    if (!editor || editor.isDestroyed) {
        return null;
    }

    const { schema, doc, selection } = editor.state;
    const linkMark = schema.marks.link;
    if (!linkMark) {
        return null;
    }

    const range = getMarkRange(doc.resolve(selection.from), linkMark);
    if (!range) {
        return null;
    }

    return range;
}

function getLinkBoundingRectFromRange(editor: Editor, range: { from: number; to: number }): DOMRect | null {
    const { from } = range;

    try {
        const { node } = editor.view.domAtPos(from);
        const el: Element | null =
            node.nodeType === Node.TEXT_NODE ? node.parentElement : node.nodeType === Node.ELEMENT_NODE ? (node as Element) : null;
        const anchor = el instanceof Element ? el.closest('a') : null;
        if (anchor) {
            return anchor.getBoundingClientRect();
        }
        const coords = editor.view.coordsAtPos(from);
        return new DOMRect(coords.left, coords.top, coords.right - coords.left, coords.bottom - coords.top);
    } catch {
        return null;
    }
}
